import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const propertySchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  address: z.string().min(1, 'L\'adresse est requise'),
  capacity: z.number()
    .min(1, 'La capacité minimum est de 1 personne')
    .max(50, 'La capacité maximum est de 50 personnes'),
  owner_id: z.string().min(1, 'Le propriétaire est requis')
});

type PropertyFormData = z.infer<typeof propertySchema>;

interface PropertyFormProps {
  property?: any;
  onClose: () => void;
  onSuccess: () => void;
}

interface Owner {
  id: string;
  first_name: string;
  last_name: string;
}

export default function PropertyForm({ property, onClose, onSuccess }: PropertyFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [owners, setOwners] = useState<Owner[]>([]);
  const { userRole } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<PropertyFormData>({
    resolver: zodResolver(propertySchema),
    defaultValues: property || { capacity: 1 }
  });

  useEffect(() => {
    fetchOwners();
  }, []);

  async function fetchOwners() {
    const { data } = await supabase
      .from('profiles')
      .select('id, first_name, last_name')
      .in('role', ['owner', 'admin', 'super_admin']);
    
    if (data) {
      setOwners(data);
    }
  }

  async function onSubmit(data: PropertyFormData) {
    try {
      setIsSubmitting(true);
      const { error } = property?.id
        ? await supabase
            .from('properties')
            .update(data)
            .eq('id', property.id)
        : await supabase
            .from('properties')
            .insert([data]);

      if (error) throw error;
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving property:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const canAssignOwner = ['admin', 'super_admin'].includes(userRole || '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {property ? 'Modifier le logement' : 'Nouveau logement'}
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nom du logement
              </label>
              <input
                type="text"
                {...register('name')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {canAssignOwner && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Propriétaire
                </label>
                <select
                  {...register('owner_id')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Sélectionner un propriétaire</option>
                  {owners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.first_name} {owner.last_name}
                    </option>
                  ))}
                </select>
                {errors.owner_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.owner_id.message}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Adresse
              </label>
              <textarea
                {...register('address')}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Capacité (nombre de personnes)
              </label>
              <input
                type="number"
                min="1"
                max="50"
                {...register('capacity', { valueAsNumber: true })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
              {errors.capacity && (
                <p className="mt-1 text-sm text-red-600">{errors.capacity.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
              >
                {isSubmitting ? 'Enregistrement...' : property ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}